import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import useClickOutside from '../../hooks/useClickOutside';

/**
 * Annotation toolbar with grouped flyout submenus (click to expand)
 */
const AnnotationToolbar = ({
  activeTool,
  onToolChange,
  annotationCount = 0,
  onClearAll,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const dropdownRef = useRef(null);

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setOpenGroup(null);
  }, []);
  useClickOutside(dropdownRef, closeDropdown);

  const isActive = activeTool !== null;
  const buttonBaseStyle = {
    backgroundColor: 'var(--dwlf-ink-20)',
    color: 'var(--dwlf-text)',
    border: 'none',
    padding: '5px 8px',
    fontSize: 12,
    cursor: 'pointer',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    transition: 'background-color 0.2s ease',
  };

  const toolGroups = [
    {
      key: 'lines',
      label: 'Lines',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="2" y1="14" x2="14" y2="2" stroke="#f97316" strokeWidth="2" strokeLinecap="round" /></svg>,
      tools: [
        { tool: 'hline', label: 'Horizontal Line', icon: <div style={{ width: 16, height: 2, backgroundColor: '#ef4444', borderRadius: 1 }} /> },
        { tool: 'vline', label: 'Vertical Line', icon: <div style={{ width: 2, height: 16, backgroundColor: '#3b82f6', borderRadius: 1 }} /> },
        { tool: 'crossline', label: 'Cross Line', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="0" y1="8" x2="16" y2="8" stroke="#06b6d4" strokeWidth="1.5" strokeDasharray="3 2" /><line x1="8" y1="0" x2="8" y2="16" stroke="#06b6d4" strokeWidth="1.5" strokeDasharray="3 2" /><circle cx="8" cy="8" r="2" fill="#06b6d4" /></svg> },
        { tool: 'trendline', label: 'Trend Line', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="2" y1="14" x2="14" y2="2" stroke="#f97316" strokeWidth="2" strokeLinecap="round" /><circle cx="2" cy="14" r="2" fill="#f97316" /><circle cx="14" cy="2" r="2" fill="#f97316" /></svg> },
        { tool: 'ray', label: 'Ray', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="2" y1="12" x2="14" y2="4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" /><circle cx="2" cy="12" r="2" fill="#22c55e" /><polygon points="14,4 10,3 11,7" fill="#22c55e" /></svg> },
      ]
    },
    {
      key: 'zones',
      label: 'Zones',
      icon: <svg width="16" height="14" viewBox="0 0 16 14" fill="none"><rect x="1" y="1" width="14" height="12" rx="1" stroke="#3b82f6" strokeWidth="1.5" fill="#3b82f6" fillOpacity="0.2" /></svg>,
      tools: [
        { tool: 'rectangle', label: 'Price Range', icon: <svg width="16" height="14" viewBox="0 0 16 14" fill="none"><rect x="1" y="1" width="14" height="12" rx="1" stroke="#3b82f6" strokeWidth="1.5" fill="#3b82f6" fillOpacity="0.2" /></svg> },
        { tool: 'timerange', label: 'Time Range', icon: <svg width="16" height="14" viewBox="0 0 16 14" fill="none"><rect x="4" y="0" width="8" height="14" fill="#8b5cf6" fillOpacity="0.3" /><line x1="4" y1="0" x2="4" y2="14" stroke="#8b5cf6" strokeWidth="1.5" /><line x1="12" y1="0" x2="12" y2="14" stroke="#8b5cf6" strokeWidth="1.5" /></svg> },
        { tool: 'channel', label: 'Parallel Channel', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><polygon points="1,12 15,4 15,0 1,8" fill="#8b5cf6" fillOpacity="0.15" /><line x1="1" y1="12" x2="15" y2="4" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" /><line x1="1" y1="8" x2="15" y2="0" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.6" /></svg> },
      ]
    },
    {
      key: 'fibonacci',
      label: 'Fibonacci',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="0" y1="1" x2="16" y2="1" stroke="#f59e0b" strokeWidth="1.5" /><line x1="0" y1="8" x2="16" y2="8" stroke="#f59e0b" strokeWidth="1" /><line x1="0" y1="15" x2="16" y2="15" stroke="#f59e0b" strokeWidth="1.5" /></svg>,
      tools: [
        { tool: 'fibRetracement', label: 'Fib Retracement', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="0" y1="1" x2="16" y2="1" stroke="#f59e0b" strokeWidth="1.5" /><line x1="0" y1="4.8" x2="16" y2="4.8" stroke="#f59e0b" strokeWidth="0.8" strokeDasharray="3 2" /><line x1="0" y1="7" x2="16" y2="7" stroke="#f59e0b" strokeWidth="0.8" strokeDasharray="3 2" /><line x1="0" y1="8.5" x2="16" y2="8.5" stroke="#f59e0b" strokeWidth="1" /><line x1="0" y1="10.5" x2="16" y2="10.5" stroke="#f59e0b" strokeWidth="0.8" strokeDasharray="3 2" /><line x1="0" y1="13" x2="16" y2="13" stroke="#f59e0b" strokeWidth="0.8" strokeDasharray="3 2" /><line x1="0" y1="15" x2="16" y2="15" stroke="#f59e0b" strokeWidth="1.5" /></svg> },
        { tool: 'pitchfork', label: 'Pitchfork', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="1" y1="8" x2="15" y2="4" stroke="#f97316" strokeWidth="1.5" strokeLinecap="round" /><line x1="5" y1="2" x2="15" y2="1" stroke="#f97316" strokeWidth="1" strokeLinecap="round" opacity="0.6" /><line x1="5" y1="14" x2="15" y2="7" stroke="#f97316" strokeWidth="1" strokeLinecap="round" opacity="0.6" /><circle cx="1" cy="8" r="1.5" fill="#f97316" /><circle cx="5" cy="2" r="1.5" fill="#f97316" /><circle cx="5" cy="14" r="1.5" fill="#f97316" /></svg> },
      ]
    },
    {
      key: 'measure',
      label: 'Measure',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="2" y1="14" x2="14" y2="2" stroke="var(--dwlf-ink-50)" strokeWidth="1.5" strokeDasharray="3 2" /><circle cx="2" cy="14" r="2" fill="var(--dwlf-ink-50)" /><circle cx="14" cy="2" r="2" fill="var(--dwlf-ink-50)" /></svg>,
      tools: [
        { tool: 'measure', label: 'Measure', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="2" y1="14" x2="14" y2="2" stroke="var(--dwlf-ink-50)" strokeWidth="1.5" strokeDasharray="3 2" /><circle cx="2" cy="14" r="2" fill="var(--dwlf-ink-50)" /><circle cx="14" cy="2" r="2" fill="var(--dwlf-ink-50)" /><line x1="2" y1="14" x2="2" y2="2" stroke="var(--dwlf-ink-50)" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5" /><line x1="2" y1="2" x2="14" y2="2" stroke="var(--dwlf-ink-50)" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5" /></svg> },
      ]
    },
    {
      key: 'position',
      label: 'Position',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="14" height="6" fill="#22c55e" fillOpacity="0.25" stroke="#22c55e" strokeWidth="0.8" /><rect x="1" y="9" width="14" height="6" fill="#ef4444" fillOpacity="0.25" stroke="#ef4444" strokeWidth="0.8" /><line x1="1" y1="8" x2="15" y2="8" stroke="var(--dwlf-ink-88)" strokeWidth="1" strokeDasharray="2 2" /></svg>,
      tools: [
        { tool: 'long_position', label: 'Long Position', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="14" height="6" fill="#22c55e" fillOpacity="0.25" stroke="#22c55e" strokeWidth="0.8" /><rect x="1" y="9" width="14" height="6" fill="#ef4444" fillOpacity="0.25" stroke="#ef4444" strokeWidth="0.8" /><line x1="1" y1="8" x2="15" y2="8" stroke="var(--dwlf-ink-88)" strokeWidth="1" strokeDasharray="2 2" /></svg> },
        { tool: 'short_position', label: 'Short Position', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="14" height="6" fill="#ef4444" fillOpacity="0.25" stroke="#ef4444" strokeWidth="0.8" /><rect x="1" y="9" width="14" height="6" fill="#22c55e" fillOpacity="0.25" stroke="#22c55e" strokeWidth="0.8" /><line x1="1" y1="8" x2="15" y2="8" stroke="var(--dwlf-ink-88)" strokeWidth="1" strokeDasharray="2 2" /></svg> },
      ]
    },
    {
      key: 'markers',
      label: 'Markers',
      icon: <span style={{ fontSize: 14 }}>T</span>,
      tools: [
        { tool: 'text', label: 'Text', icon: <span style={{ fontSize: 14 }}>T</span> },
        { tool: 'emoji', label: 'Emoji', icon: <span style={{ fontSize: 14 }}>😀</span> },
        { tool: 'arrow', label: 'Arrow/Callout', icon: <svg width="16" height="14" viewBox="0 0 16 14" fill="none"><line x1="2" y1="12" x2="12" y2="3" stroke="#f97316" strokeWidth="1.5" /><polyline points="8,2 13,2 13,7" fill="none" stroke="#f97316" strokeWidth="1.5" strokeLinejoin="round" /></svg> },
      ]
    },
    {
      key: 'alerts',
      label: 'Alerts',
      icon: <span style={{ fontSize: 14 }}>🔔</span>,
      tools: [
        { tool: 'alert_line', label: 'Alert Line', icon: <span style={{ fontSize: 14 }}>🔔</span> },
      ]
    },
    {
      key: 'drawing',
      label: 'Drawing',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 14 C4 10, 6 4, 8 6 C10 8, 10 12, 14 2" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" fill="none" /></svg>,
      tools: [
        { tool: 'brush', label: 'Freehand Draw', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 14 C4 10, 6 4, 8 6 C10 8, 10 12, 14 2" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" fill="none" /></svg> },
      ]
    },
    {
      key: 'vwap',
      label: 'VWAP',
      icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="1" y1="13" x2="15" y2="5" stroke="#B388FF" strokeWidth="1.75" strokeLinecap="round" /><line x1="1" y1="16" x2="15" y2="8" stroke="#9575CD" strokeWidth="1" strokeDasharray="3 2" /><line x1="1" y1="10" x2="15" y2="2" stroke="#9575CD" strokeWidth="1" strokeDasharray="3 2" /><circle cx="1" cy="13" r="2" fill="#B388FF" /></svg>,
      tools: [
        { tool: 'anchored_vwap', label: 'Anchored VWAP', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="1" y1="13" x2="15" y2="5" stroke="#B388FF" strokeWidth="1.75" strokeLinecap="round" /><line x1="1" y1="16" x2="15" y2="8" stroke="#9575CD" strokeWidth="1" strokeDasharray="3 2" /><line x1="1" y1="10" x2="15" y2="2" stroke="#9575CD" strokeWidth="1" strokeDasharray="3 2" /><circle cx="1" cy="13" r="2" fill="#B388FF" /></svg> },
      ]
    },
  ];

  // Check if a group contains the active tool
  const groupContainsActive = (group) => group.tools.some((t) => t.tool === activeTool);

  const handleGroupClick = (groupKey) => {
    setOpenGroup((prev) => (prev === groupKey ? null : groupKey));
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        onClick={() => {
          const newShowState = !showDropdown;
          setShowDropdown(newShowState);
          if (!newShowState) {
            setOpenGroup(null);
          }
        }}
        style={{
          ...buttonBaseStyle,
          backgroundColor: isActive ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-20)',
          color: isActive ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
        }}
        title="Annotations"
      >
        <span>Annotations</span>
        <span style={{ fontSize: 10 }}>▼</span>
      </button>

      {showDropdown && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 2,
            backgroundColor: 'var(--dwlf-elevated)',
            border: '1px solid var(--dwlf-ink-30)',
            borderRadius: 4,
            minWidth: 160,
            boxShadow: '0 8px 24px rgb(0 0 0 / 0.3)',
            zIndex: 1000,
          }}
        >
          {/* Group list */}
          {toolGroups.map((group) => {
            const isOpen = openGroup === group.key;
            const hasActive = groupContainsActive(group);

            return (
              <div
                key={group.key}
                style={{ position: 'relative' }}
              >
                {/* Group row — click to toggle flyout */}
                <div
                  onClick={() => handleGroupClick(group.key)}
                  style={{
                    padding: '9px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 12,
                    color: 'var(--dwlf-ink-88)',
                    backgroundColor: isOpen
                      ? 'var(--dwlf-ink-10)'
                      : hasActive
                        ? 'var(--dwlf-accent-15)'
                        : 'transparent',
                    borderBottom: '1px solid var(--dwlf-ink-12)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isOpen) {
                      e.currentTarget.style.backgroundColor = hasActive
                        ? 'var(--dwlf-accent-25)'
                        : 'var(--dwlf-ink-05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isOpen
                      ? 'var(--dwlf-ink-10)'
                      : hasActive
                        ? 'var(--dwlf-accent-15)'
                        : 'transparent';
                  }}
                >
                  <div style={{ width: 20, display: 'flex', justifyContent: 'center' }}>
                    {group.icon}
                  </div>
                  <span style={{ flex: 1 }}>{group.label}</span>
                  {hasActive && (
                    <span style={{ color: 'var(--dwlf-accent)', fontSize: 8 }}>●</span>
                  )}
                  <span style={{ color: 'var(--dwlf-ink-40)', fontSize: 10 }}>›</span>
                </div>

                {/* Flyout submenu */}
                {isOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '100%',
                      top: 0,
                      marginLeft: 2,
                      backgroundColor: 'var(--dwlf-elevated)',
                      border: '1px solid var(--dwlf-ink-30)',
                      borderRadius: 4,
                      minWidth: 170,
                      boxShadow: '0 8px 24px rgb(0 0 0 / 0.3)',
                      zIndex: 1001,
                    }}
                  >
                    {group.tools.map((toolItem, idx) => {
                      const isToolActive = activeTool === toolItem.tool;
                      return (
                        <div
                          key={toolItem.tool}
                          onClick={() => {
                            onToolChange(isToolActive ? null : toolItem.tool);
                            setShowDropdown(false);
                            setOpenGroup(null);
                          }}
                          style={{
                            padding: '9px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            fontSize: 12,
                            color: 'var(--dwlf-ink-88)',
                            backgroundColor: isToolActive ? 'var(--dwlf-accent-30)' : 'transparent',
                            borderBottom: idx < group.tools.length - 1 ? '1px solid var(--dwlf-ink-12)' : 'none',
                          }}
                          onMouseEnter={(e) => {
                            if (!isToolActive) {
                              e.currentTarget.style.backgroundColor = 'var(--dwlf-ink-10)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isToolActive ? 'var(--dwlf-accent-30)' : 'transparent';
                          }}
                        >
                          <div style={{ width: 20, display: 'flex', justifyContent: 'center' }}>
                            {toolItem.icon}
                          </div>
                          <span>{toolItem.label}</span>
                          {isToolActive && (
                            <span style={{ marginLeft: 'auto', color: 'var(--dwlf-accent)', fontSize: 10 }}>●</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Clear All */}
          {annotationCount > 0 && (
            <>
              <div style={{ borderTop: '1px solid var(--dwlf-ink-30)', margin: '2px 0' }} />
              <div
                onClick={() => {
                  onClearAll?.();
                  setShowDropdown(false);
                  setOpenGroup(null);
                }}
                style={{
                  padding: '9px 12px',
                  cursor: 'pointer',
                  fontSize: 12,
                  color: 'var(--dwlf-down)',
                  textAlign: 'center',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--dwlf-down-10)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Clear All ({annotationCount})
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

AnnotationToolbar.propTypes = {
  activeTool: PropTypes.oneOf(['hline', 'vline', 'crossline', 'rectangle', 'text', 'trendline', 'ray', 'timerange', 'emoji', 'arrow', 'channel', 'fibRetracement', 'measure', 'alert_line', 'brush', 'pitchfork', 'long_position', 'short_position', null]),
  onToolChange: PropTypes.func.isRequired,
  annotationCount: PropTypes.number,
  onClearAll: PropTypes.func,
};

export default AnnotationToolbar;
